import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";

// Reusable Kanban board.
//
// Props:
//   columns: [{ key, title, color?, count? }]
//   cardsByColumn: { [colKey]: [card, ...] }
//   onMove: (cardId, fromKey, toKey, toIndex) => Promise|void
//   renderCard: (card, colKey) => JSX
//   renderKebab?: (card, colKey) => JSX   (fully custom kebab; renders inside the card)
//   testid: prefix for data-testids
export function KanbanBoard({ columns, cardsByColumn, onMove, renderCard, renderKebab, testid = "kanban" }) {
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    await onMove(draggableId, source.droppableId, destination.droppableId, destination.index);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid gap-3 auto-cols-[minmax(240px,1fr)] grid-flow-col overflow-x-auto pb-2">
        {columns.map((col) => {
          const items = cardsByColumn[col.key] || [];
          return (
            <Droppable key={col.key} droppableId={col.key}>
              {(prov, snap) => (
                <div
                  ref={prov.innerRef}
                  {...prov.droppableProps}
                  data-testid={`${testid}-column-${col.key}`}
                  className={`rounded-md border ${snap.isDraggingOver ? "border-neutral-500 bg-neutral-50" : "border-neutral-200 bg-white"} p-2 flex flex-col min-h-[300px]`}
                >
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                      {col.color && <span className="h-2 w-2 rounded-full" style={{ background: col.color }} />}
                      <p className="text-xs font-semibold text-neutral-700">{col.title}</p>
                    </div>
                    <span className="text-[10px] mono text-neutral-500">{items.length}</span>
                  </div>
                  <div className="space-y-2 flex-1">
                    {items.map((card, idx) => (
                      <Draggable key={card.id} draggableId={String(card.id)} index={idx}>
                        {(dp, sn) => (
                          <div
                            ref={dp.innerRef}
                            {...dp.draggableProps}
                            data-testid={`${testid}-card-${card.id}`}
                            className={`rounded-md border bg-white p-3 group text-left transition-shadow ${
                              sn.isDragging ? "shadow-lg border-neutral-500" : "border-neutral-200"
                            }`}
                            style={dp.draggableProps.style}
                          >
                            <div className="flex items-start gap-1">
                              <button
                                {...dp.dragHandleProps}
                                className="opacity-0 group-hover:opacity-100 shrink-0 text-neutral-400 cursor-grab pt-0.5"
                                aria-label="Drag"
                              >
                                <GripVertical size={13} />
                              </button>
                              <div className="flex-1 min-w-0">{renderCard(card, col.key)}</div>
                              {renderKebab && (
                                <div className="opacity-0 group-hover:opacity-100 shrink-0">
                                  {renderKebab(card, col.key)}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {prov.placeholder}
                    {items.length === 0 && !snap.isDraggingOver && (
                      <p className="text-[11px] italic text-neutral-400 text-center py-4">Empty</p>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
}
